import { message } from "utils/ciq-message";
import { IconWifi, IconWifiOff } from "@tabler/icons-react";
import { ErrorMessages } from "../constants";
import { onConnectivityChange } from "./connectivity-state";

/**
 * One antd key so the offline notice and the back-online confirmation replace
 * each other instead of stacking.
 */
const CONNECTIVITY_TOAST_KEY = "connectivity";

/**
 * Hold the browser-offline notice long enough for a blip (AP roaming, sleep
 * transitions) to resolve itself — a notice that flashes and retracts reads
 * as instability. The heartbeat-derived path needs no extra hold: its
 * failure threshold IS the wait. Recovery is always announced immediately.
 */
const OFFLINE_NOTICE_HOLD_MS = 3_000;

// Offline is a state, not a fault — neutral theme gray for the notice, theme
// success green for the recovery. Tabler icons stroke with currentColor, so
// the palette classes carry the color.
const OFFLINE_ICON_CLASS = "text-ciq-gray-500";
const ONLINE_ICON_CLASS = "text-ciq-green-600";

/**
 * Connectivity notices from BOTH signals — browser online/offline events
 * (hard offline) and the heartbeat-derived state (Wi-Fi off behind virtual
 * adapters, where navigator.onLine stays true): a sticky "you're offline"
 * toast while disconnected (per-request failure toasts auto-dismiss, so
 * without this the user has no persistent signal), replaced by a short
 * confirmation when the connection returns. Returns a disposer.
 */
export function installConnectivityNotices(): () => void {
  let wasOffline = false;
  let pendingOfflineNotice: ReturnType<typeof setTimeout> | null = null;

  const cancelPendingOfflineNotice = () => {
    if (pendingOfflineNotice) {
      clearTimeout(pendingOfflineNotice);
      pendingOfflineNotice = null;
    }
  };

  const showOffline = () => {
    cancelPendingOfflineNotice();
    wasOffline = true;
    message.error({
      content: ErrorMessages.NetworkError.offlineMessage,
      duration: 0,
      key: CONNECTIVITY_TOAST_KEY,
      icon: (
        <IconWifiOff
          size={16}
          className={`mr-2 inline-block align-text-bottom ${OFFLINE_ICON_CLASS}`}
        />
      )
    });
  };

  const showOnline = () => {
    // A reconnect during the hold means the blip resolved itself — cancel the
    // pending notice and say nothing.
    cancelPendingOfflineNotice();
    // Only announce a RECOVERY — an online event without a preceding offline
    // (e.g. right after install) would just be noise.
    if (!wasOffline) {
      return;
    }
    wasOffline = false;
    message.success({
      content: ErrorMessages.NetworkError.backOnline,
      duration: 3,
      key: CONNECTIVITY_TOAST_KEY,
      icon: (
        <IconWifi
          size={16}
          className={`mr-2 inline-block align-text-bottom ${ONLINE_ICON_CLASS}`}
        />
      )
    });
  };

  const onBrowserOffline = () => {
    if (pendingOfflineNotice || wasOffline) {
      return;
    }
    pendingOfflineNotice = setTimeout(() => {
      pendingOfflineNotice = null;
      showOffline();
    }, OFFLINE_NOTICE_HOLD_MS);
  };

  const unsubscribeDerived = onConnectivityChange((offline) =>
    offline ? showOffline() : showOnline()
  );
  window.addEventListener("offline", onBrowserOffline);
  window.addEventListener("online", showOnline);
  return () => {
    cancelPendingOfflineNotice();
    unsubscribeDerived();
    window.removeEventListener("offline", onBrowserOffline);
    window.removeEventListener("online", showOnline);
  };
}
